"use client"

import { Box, Button, Fade, FormControlLabel, IconButton, Modal, NoSsr, Skeleton, Switch, TextField, Typography, colors, styled, useTheme } from "@mui/material"
import api from "@v2/lib/api"
import axios, { AxiosError } from "@v2/lib/axios"
import { useAppSelector } from "@v2/lib/redux/hooks"
import { MessageSquareShare, X } from "lucide-react"
import Link from "next/link"
import { usePathname, useSearchParams } from "next/navigation"
import { useSnackbar } from "notistack"
import { ChangeEvent, useCallback, useState } from "react"

type VideoAddCommentButtonProps = {
    videoId: number
    onSubmitted?: () => void
}

export default function VideoAddCommentButton({
    videoId,
    onSubmitted
}: VideoAddCommentButtonProps) {
    const session = useAppSelector(s => s.general.session)
    const isAuth = session?.isAuthenticated ?? false
    const pathname = usePathname()
    const searchParams = useSearchParams()
    const currentPath = pathname + (searchParams.size > 0 ? `?${searchParams.toString()}` : "")
    const signInHref = `/fa/sign-in?callback_url=${encodeURIComponent(`${currentPath}#comments`)}`
    const { enqueueSnackbar } = useSnackbar()
    const theme = useTheme()

    const [modalIsOpen, setModalIsOpen] = useState(false)
    const [commentText, setCommentText] = useState<string | null>(null)
    const [isUnknown, setIsUnknown] = useState(false)
    const [submitInProcess, setSubmitInProcess] = useState(false)

    const onClose = useCallback(() => {
        setModalIsOpen(false)
        setCommentText(null)
        setIsUnknown(false)
    }, [])

    const onOpen = useCallback(() => {
        if (!isAuth) {
            return enqueueSnackbar({
                message: (
                    <Box>
                        <Typography sx={{ fontSize: 14 }}>برای ثبت دیدگاه ابتدا وارد حساب کاربری شود</Typography>
                        <Button
                            color="inherit"
                            variant="outlined"
                            component={Link}
                            prefetch={false}
                            href={signInHref}
                            sx={{ mt: 1, minWidth: 68 }}
                        >
                            ورود
                        </Button>
                    </Box>
                )
            })
        }

        setModalIsOpen(true)
    }, [enqueueSnackbar, isAuth, signInHref])

    const onChangeTextField = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
        setCommentText(event.target.value || null)
    }, [])

    const onChangeUnknownUser = useCallback((_event: unknown, value: boolean) => {
        setIsUnknown(value)
    }, [])

    const submitComment = useCallback(() => {
        if (!commentText || commentText.length < 10) return

        setSubmitInProcess(true)
        axios.post(api.videoComments(videoId), {
            text: commentText,
            is_unknown: isUnknown
        }).then(() => {
            setSubmitInProcess(false)
            onClose()
            onSubmitted?.()
            enqueueSnackbar("دیدگاه شما ثبت شد و پس از بررسی منتشر خواهد شد", {
                autoHideDuration: 8000,
                variant: "success"
            })
        }).catch((error: AxiosError) => {
            setSubmitInProcess(false)
            enqueueSnackbar(error.response?.data?.message ?? "خطا در ارسال دیدگاه", {
                autoHideDuration: 8000,
                variant: "error"
            })
        })
    }, [commentText, enqueueSnackbar, isUnknown, onClose, onSubmitted, videoId])

    return (
        <NoSsr fallback={<Skeleton variant="rounded" height={48} width={114} />}>
            <Modal open={modalIsOpen} onClose={onClose}>
                <Box sx={{
                    bgcolor: "#fff",
                    maxWidth: 560,
                    margin: "auto",
                    marginTop: 6,
                    borderRadius: 1,
                    [theme.breakpoints.down("md")]: {
                        position: "absolute",
                        right: 0,
                        left: 0,
                        maxWidth: "unset",
                        bottom: 0
                    }
                }}>
                    <Box sx={{
                        position: "relative",
                        borderBottom: "1px solid",
                        borderColor: colors.grey[300],
                        height: 56,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}>
                        <Typography sx={{ fontSize: 18, fontWeight: 400 }}>ثبت دیدگاه</Typography>
                        <IconButton sx={{ height: 32, width: 32, position: "absolute", left: 16 }} onClick={onClose}>
                            <X />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 2 }}>
                        <Title>دیدگاه شما درباره ویدیو</Title>
                        <TextField
                            onChange={onChangeTextField}
                            autoFocus
                            multiline
                            rows={4}
                            fullWidth
                            placeholder="دیدگاه خود را بنویسید ..."
                            sx={{ mt: 2 }}
                        />

                        <Box sx={{ mt: 2 }}>
                            <FormControlLabel
                                sx={{
                                    flexDirection: "row-reverse",
                                    width: "100%",
                                    justifyContent: "space-between",
                                    m: 0,
                                    userSelect: "none",
                                    "& .MuiFormControlLabel-label": {
                                        fontSize: 13
                                    }
                                }}
                                onChange={onChangeUnknownUser}
                                control={<Switch />}
                                label="ارسال دیدگاه به صورت ناشناس"
                            />
                        </Box>

                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            disableElevation
                            disabled={!commentText || commentText.length < 10}
                            onClick={submitComment}
                            endIcon={!submitInProcess ? <MessageSquareShare color="#fff" /> : undefined}
                            loading={submitInProcess}
                            sx={{ height: 48, mt: 3 }}
                        >
                            ارسال دیدگاه
                        </Button>

                        <Typography sx={{
                            fontSize: 11,
                            mt: 1,
                            textAlign: "center",
                            "& > a": {
                                textDecoration: "underline",
                                color: colors.red[900]
                            }
                        }}>
                            ارسال دیدگاه به معنی موافقت با <a href="/fa/pages/terms-and-conditions" target="_blank">قوانین و مقررات عینک وحدت</a> است.
                        </Typography>
                    </Box>
                </Box>
            </Modal>

            <Fade in={true}>
                <Button color="primary" variant="contained" disableElevation onClick={onOpen} sx={{ height: 48, minWidth: 114 }}>
                    افزودن دیدگاه
                </Button>
            </Fade>
        </NoSsr>
    )
}

const Title = styled(Typography)({
    fontWeight: 500
})
